import type { IResource } from "@gettactic/api";
import {
  dedicatedSeatCount,
  isDedicatedFull
} from "@gettactic/helpers/src/resources/dedicatedSeats";

export interface DedicatedOwnersText {
  /** "2 of 4 seats" on a shared workspace; null on a single seat. */
  seatCounter: string | null;
  /** Why the picker is hidden on a full shared workspace; null otherwise. */
  fullHint: string | null;
  /** Under each owner's name. */
  ownerCaption: string;
  /** Above the picker. */
  pickerPrompt: string;
}

/**
 * The owners section's wording. A single seat keeps the wording it always
 * had: the shared-workspace wording only appears when there is more than one seat.
 */
export const dedicatedOwnersText = (
  resource: Pick<IResource, "type" | "total_slots">,
  ownerCount: number
): DedicatedOwnersText => {
  const seatCount = dedicatedSeatCount(resource);
  if (seatCount === 1) {
    const noun = resource.type === "parking_space" ? "space" : "desk";
    return {
      seatCounter: null,
      fullHint: null,
      ownerCaption: `This ${noun} belongs to them — nobody else can book it.`,
      pickerPrompt: `Pick the person this ${noun} belongs to. It won't be bookable by anyone else.`
    };
  }
  return {
    seatCounter: `${ownerCount} of ${seatCount} seats`,
    fullHint: isDedicatedFull(resource, ownerCount)
      ? "All seats are taken. Increase capacity in General to add more."
      : null,
    ownerCaption: "One of the seats is theirs — nobody else can book it.",
    pickerPrompt:
      "Add a person. Each owner gets one seat, and nobody else can book them."
  };
};
